import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import './ProfileForm.css';

const SERVICE_OPTIONS = [
    { value: 'electrician', label: 'Electrician' },
    { value: 'plumber', label: 'Plumber' },
    { value: 'carpenter', label: 'Carpenter' },
    { value: 'painter', label: 'Painter' },
    { value: 'ac-repair', label: 'AC Repair' },
    { value: 'cleaning', label: 'Cleaning' },
    { value: 'glass-windows', label: 'Glass & Windows' },
    { value: 'home-renovation', label: 'Home Renovation' },
];

const defaultForm = {
    name: '',
    phone: '',
    serviceCategory: '',
    experience: '',
    hourlyRate: '',
    location: '',
    bio: '',
    skills: '',
    certifications: '',
    availWeekdays: true,
    availWeekends: false,
    availEmergency: false,
};

const ProfileForm = ({ onProfileSaved }) => {
    const { currentUser } = useAuth();
    const [form, setForm] = useState(defaultForm);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [geoCoords, setGeoCoords] = useState(null);
    const [geoStatus, setGeoStatus] = useState(''); // '', 'loading', 'success', 'error'

    // Load existing profile from Firestore on mount
    useEffect(() => {
        if (!currentUser) return;
        const load = async () => {
            try {
                const snap = await getDoc(doc(db, 'serviceProviders', currentUser.uid));
                if (snap.exists()) {
                    const data = snap.data();
                    setForm({
                        name: data.name || '',
                        phone: data.phone || '',
                        serviceCategory: data.serviceCategory || '',
                        experience: data.experience !== undefined ? String(data.experience) : '',
                        hourlyRate: data.hourlyRate !== undefined ? String(data.hourlyRate) : '',
                        location: data.location || '',
                        bio: data.bio || '',
                        skills: Array.isArray(data.skills) ? data.skills.join(', ') : (data.skills || ''),
                        certifications: data.certifications || '',
                        availWeekdays: data.availability?.weekdays ?? true,
                        availWeekends: data.availability?.weekends ?? false,
                        availEmergency: data.availability?.emergency ?? false,
                    });
                    if (data.latitude && data.longitude) {
                        setGeoCoords({ latitude: data.latitude, longitude: data.longitude });
                        setGeoStatus('success');
                    }
                }
            } catch (e) {
                console.error('Failed to load profile:', e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [currentUser]);

    const handleChange = (e) => {
        const { id, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [id]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!currentUser) return;
        setSaving(true);
        setSuccess(false);
        setError('');
        try {
            const skillsArray = form.skills
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);

            await setDoc(doc(db, 'serviceProviders', currentUser.uid), {
                name: form.name,
                phone: form.phone,
                email: currentUser.email,
                serviceCategory: form.serviceCategory,
                experience: Number(form.experience) || 0,
                hourlyRate: Number(form.hourlyRate) || 0,
                location: form.location,
                bio: form.bio,
                skills: skillsArray,
                certifications: form.certifications,
                latitude: geoCoords?.latitude || null,
                longitude: geoCoords?.longitude || null,
                availability: {
                    weekdays: form.availWeekdays,
                    weekends: form.availWeekends,
                    emergency: form.availEmergency,
                },
                updatedAt: new Date().toISOString(),
            }, { merge: true });

            setSuccess(true);
            if (onProfileSaved) onProfileSaved();
            setTimeout(() => setSuccess(false), 3000);
        } catch (e) {
            console.error('Failed to save profile:', e);
            setError('Failed to save profile. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="profile-form"><p className="form-loading">Loading profile…</p></div>;
    }

    return (
        <div className="profile-form">
            <h3>Edit Profile</h3>

            {success && <div className="form-alert form-alert-success">✅ Profile saved successfully! Customers can now find you.</div>}
            {error && <div className="form-alert form-alert-error">{error}</div>}

            <form onSubmit={handleSubmit}>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="name">Full Name</label>
                        <input
                            type="text"
                            id="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="Enter your name"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <input
                            type="tel"
                            id="phone"
                            value={form.phone}
                            onChange={handleChange}
                            placeholder="Enter your phone"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="serviceCategory">Service Category</label>
                        <select id="serviceCategory" value={form.serviceCategory} onChange={handleChange} required>
                            <option value="">— Select a service —</option>
                            {SERVICE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="experience">Years of Experience</label>
                        <input
                            type="number"
                            id="experience"
                            value={form.experience}
                            onChange={handleChange}
                            min="0"
                            placeholder="e.g. 5"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="hourlyRate">Hourly Rate (₹)</label>
                        <input
                            type="number"
                            id="hourlyRate"
                            value={form.hourlyRate}
                            onChange={handleChange}
                            min="0"
                            placeholder="e.g. 450"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="location">Location / Area</label>
                        <input
                            type="text"
                            id="location"
                            value={form.location}
                            onChange={handleChange}
                            placeholder="e.g. Bangalore, HSR Layout"
                        />
                        <button
                            type="button"
                            className="geo-btn"
                            onClick={() => {
                                if (!navigator.geolocation) {
                                    setGeoStatus('error');
                                    return;
                                }
                                setGeoStatus('loading');
                                navigator.geolocation.getCurrentPosition(
                                    async (pos) => {
                                        const { latitude, longitude } = pos.coords;
                                        setGeoCoords({ latitude, longitude });
                                        try {
                                            const res = await fetch(
                                                `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
                                                { headers: { 'Accept-Language': 'en' } }
                                            );
                                            const data = await res.json();
                                            const addr = data.address || {};
                                            const area = addr.suburb || addr.neighbourhood || addr.road || '';
                                            const city = addr.city || addr.town || addr.village || '';
                                            const readable = [area, city].filter(Boolean).join(', ');
                                            if (readable) {
                                                setForm(prev => ({ ...prev, location: readable }));
                                            }
                                        } catch {
                                            // coords saved even if reverse geocode fails
                                        }
                                        setGeoStatus('success');
                                    },
                                    () => setGeoStatus('error'),
                                    { enableHighAccuracy: true, timeout: 10000 }
                                );
                            }}
                            disabled={geoStatus === 'loading'}
                        >
                            {geoStatus === 'loading' ? '📡 Getting location…' :
                                geoStatus === 'success' ? '✅ Location saved' :
                                    geoStatus === 'error' ? '❌ Retry location' :
                                        '📍 Use My Current Location'}
                        </button>
                        {geoStatus === 'success' && geoCoords && (
                            <small className="geo-hint">
                                GPS coordinates saved — customers can find you by proximity!
                            </small>
                        )}
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="bio">Bio</label>
                    <textarea
                        id="bio"
                        rows="4"
                        value={form.bio}
                        onChange={handleChange}
                        placeholder="Tell customers about yourself, your experience, and what makes you great at your job"
                    ></textarea>
                </div>

                <div className="form-group">
                    <label htmlFor="skills">Skills <small>(comma-separated)</small></label>
                    <input
                        type="text"
                        id="skills"
                        value={form.skills}
                        onChange={handleChange}
                        placeholder="e.g. Wiring, Panel Repair, LED Installation"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="certifications">Certifications</label>
                    <input
                        type="text"
                        id="certifications"
                        value={form.certifications}
                        onChange={handleChange}
                        placeholder="e.g. Licensed Electrician, OSHA Certified"
                    />
                </div>

                <div className="form-group">
                    <label>Availability</label>
                    <div className="checkbox-group">
                        <label className="checkbox-label">
                            <input type="checkbox" id="availWeekdays" checked={form.availWeekdays} onChange={handleChange} />
                            <span>Monday – Friday</span>
                        </label>
                        <label className="checkbox-label">
                            <input type="checkbox" id="availWeekends" checked={form.availWeekends} onChange={handleChange} />
                            <span>Weekends</span>
                        </label>
                        <label className="checkbox-label">
                            <input type="checkbox" id="availEmergency" checked={form.availEmergency} onChange={handleChange} />
                            <span>Emergency Services</span>
                        </label>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                        {saving ? 'Saving…' : 'Save Profile'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ProfileForm;
